/**
 * Analyze tab — Analytical Model panel tools (BIM "Analytical Model").
 *
 * These tools edit the line/frame analytical model held by
 * analyticalModelManager (consumed by FEASolver), not physical elements:
 * - Analytical Member: pick a column/beam/brace, create its 1D axis member.
 * - Analytical Panel:  pick a floor/roof/wall, create its 2D surface.
 * - Boundary Conditions: pick an analytical node, assign a support type.
 * - Analytical Automation: rebuild the whole model from the physical model
 *   and validate it (the Dynamo-style one-click conversion in BIM).
 */

import { v4 as uuidv4 } from 'uuid';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { BaseTool } from '../BaseTool';
import { analyticalModelManager, rebuildAnalyticalModel } from '../../core/services';
import { useAnalysisStore } from '../../state/useAnalysisStore';
import { BoundaryCondition } from '../../core/AnalyticalModelManager';
import i18n from '../../i18n';

const PHYSICAL_CATEGORIES = [
    'Structural Columns',
    'Structural Framing',
    'Structural Trusses',
    'Walls',
    'Floors',
    'Roofs',
];

function isAnalyticalHost(el: any): boolean {
    return PHYSICAL_CATEGORIES.includes(el?.category);
}

/**
 * Shared helper: notify through the global toast host (same event as
 * BaseTool.notify) and refresh the analytical overlay version so the
 * viewport re-reads the model after an edit.
 */
function finishModelEdit(message: string, status: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    window.dispatchEvent(new CustomEvent('yap:notify', { detail: { message, status } }));
    useAnalysisStore.getState().bumpAnalyticalModel();
}

export class AnalyticalMemberTool extends BaseTool {
    id = 'ANALYZE_ANALYTICAL_MEMBER';
    name = 'Analytical Member';
    cursor = 'crosshair';

    onActivate(): void {
        this.notify(i18n.t('tools.notify.pickAColumnBeamOrBrace'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const hit = this.findElementHitAtPoint(event, isAnalyticalHost);
        if (!hit) {
            this.notify(i18n.t('tools.notify.noStructuralElementUnderTheCursor'), 'warning');
            return;
        }
        const { element } = hit;
        const existing = analyticalModelManager.getMemberByElementId(element.id);
        if (existing) {
            finishModelEdit(`Analytical member for ${element.type} already exists (edit its releases instead).`, 'warning');
            return;
        }
        const [start, end] = memberExtentsOf(element);
        if (!start || !end) {
            finishModelEdit('Selected element has no usable analytical extents.', 'error');
            return;
        }
        const materialName = inferMaterialName(element);
        analyticalModelManager.createMemberFromElement(element.id, start, end, 'W12x26', materialName);
        finishModelEdit(`Analytical member created for ${element.type}.`, 'success');
    }

    onPointerMove(): void { }
    onPointerUp(): void { }
}

export class AnalyticalPanelTool extends BaseTool {
    id = 'ANALYZE_ANALYTICAL_PANEL';
    name = 'Analytical Panel';
    cursor = 'crosshair';

    onActivate(): void {
        this.notify(i18n.t('tools.notify.pickAFloorRoofOrWall'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const hit = this.findElementHitAtPoint(event, el =>
            ['Floors', 'Roofs', 'Walls', 'Structural Columns'].includes((el as any).category)
        );
        if (!hit) {
            this.notify(i18n.t('tools.notify.noPlanarElementUnderTheCursor'), 'warning');
            return;
        }
        const { element } = hit;
        const boundary = surfaceBoundaryOf(element);
        if (!boundary || boundary.length < 3) {
            finishModelEdit('Selected element has no extractable surface boundary.', 'error');
            return;
        }
        const thickness = Number((element as any).geometryData?.thickness ?? 0.2);
        analyticalModelManager.createSurfaceFromElement(element.id, boundary, thickness);
        finishModelEdit(`Analytical surface created for ${element.type}.`, 'success');
    }

    onPointerMove(): void { }
    onPointerUp(): void { }
}

export class BoundaryConditionsTool extends BaseTool {
    id = 'ANALYZE_BOUNDARY_CONDITIONS';
    name = 'Boundary Conditions';
    cursor = 'crosshair';

    /** Which support the next click applies; read from tool properties. */
    private currentCondition(): BoundaryCondition {
        return (this.context?.bimStore.toolProperties.boundaryConditionType as BoundaryCondition) ?? 'Pinned';
    }

    onActivate(): void {
        this.notify(i18n.t('tools.notify.bcPickNode', { support: this.currentCondition() }), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const pos: [number, number, number] = [point.x, point.y, point.z];
        // Widen the pick tolerance so clicking near a member end still snaps.
        let node = analyticalModelManager.findNodeAtPosition(pos, 0.25);
        const condition = this.currentCondition();
        if (!node) {
            // BIM lets you add a new support node at an arbitrary location.
            node = analyticalModelManager.createNode(pos, condition);
            finishModelEdit(`New ${condition} support node created.`, 'success');
            return;
        }
        analyticalModelManager.setBoundaryCondition(node.id, condition);
        finishModelEdit(`${condition} support assigned to analytical node.`, 'success');
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const node = analyticalModelManager.findNodeAtPosition([point.x, point.y, point.z], 0.25);
        if (node) {
            this.context.appStore.setToolPreview({
                type: 'point',
                position: new THREE.Vector3(...node.position),
                color: '#2f6fb8',
            });
        } else {
            this.clearPreview();
        }
    }

    onPointerUp(): void { }
}

export class AnalyticalAutomationTool extends BaseTool {
    id = 'ANALYZE_ANALYTICAL_AUTOMATION';
    name = 'Analytical Automation';
    cursor = 'default';

    onActivate(): void {
        rebuildAnalyticalModel();
        const model = analyticalModelManager.getModel();
        const members = model.members.size;
        const nodes = model.nodes.size;
        const surfaces = model.surfaces.size;
        if (analyticalModelManager.isModelValid()) {
            finishModelEdit(
                `Analytical model rebuilt automatically: ${members} members, ${nodes} nodes, ${surfaces} surfaces.`,
                'success'
            );
        } else {
            const errors = analyticalModelManager.getErrorMessages();
            finishModelEdit(
                `Analytical model rebuilt with ${errors.length} issue(s): ${errors.slice(0, 3).join(' | ') || 'unknown'}.`,
                'warning'
            );
        }
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}

/** Show / hide the analytical overlay (BIM's "Show Analytical Model"). */
export class ShowAnalyticalModelTool extends BaseTool {
    id = 'ANALYZE_SHOW_ANALYTICAL';
    name = 'Show Analytical Model';
    cursor = 'default';

    onActivate(): void {
        const store = useAnalysisStore.getState();
        store.toggleAnalyticalModel();
        this.notify(i18n.t('tools.notify.analyticalDisplayToggled', { state: store.showAnalyticalModel ? i18n.t('tools.notify.stateDisabled') : i18n.t('tools.notify.stateEnabled') }), 'info');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}

// ==================== Geometry helpers ====================

/** Start/end axis points extracted from a physical structural element. */
function memberExtentsOf(el: any): [[number, number, number] | null, [number, number, number] | null] {
    const g = el.geometryData;
    if (!g) return [null, null];
    if (el.category === 'Structural Columns') {
        // ColumnTool stores basePoint/topPoint; the core type stores location + height.
        if (Array.isArray(g.basePoint) && Array.isArray(g.topPoint)) {
            return [g.basePoint as [number, number, number], g.topPoint as [number, number, number]];
        }
        const loc = g.location;
        if (Array.isArray(loc)) {
            const z0 = Number(g.bottomOffset ?? 0);
            const z1 = Number(loc[2] ?? 0) + Number(g.height ?? 0) + Number(g.topOffset ?? 0);
            return [[loc[0], loc[1], z0], [loc[0], loc[1], z1]];
        }
    }
    if ((el.category === 'Structural Framing' || el.category === 'Structural Trusses') && Array.isArray(g.start) && Array.isArray(g.end)) {
        return [g.start as [number, number, number], g.end as [number, number, number]];
    }
    return [null, null];
}

/** 3D boundary polygon for a planar element, used by analytical surfaces. */
function surfaceBoundaryOf(el: any): [number, number, number][] | null {
    const g = el.geometryData;
    if (!g) return null;
    const levelZ = Number(g.levelZ ?? g.heightOffsetFromLevel ?? g.position?.[2] ?? 0);
    if (Array.isArray(g.boundary) && g.boundary.length >= 3) {
        return g.boundary.map((p: any) =>
            Array.isArray(p)
                ? [Number(p[0]), Number(p[1]), levelZ] as [number, number, number]
                : [p.x, p.y, levelZ] as [number, number, number]
        );
    }
    if (Array.isArray(g.start) && Array.isArray(g.end) && el.category === 'Walls') {
        const h = Number(g.height ?? 3);
        const [x1, y1] = g.start;
        const [x2, y2] = g.end;
        const z0 = Number(g.baseOffset ?? 0) / (Number(g.baseOffset ?? 0) > 100 ? 1000 : 1);
        return [
            [x1, y1, z0], [x2, y2, z0], [x2, y2, z0 + h], [x1, y1, z0 + h],
        ] as [number, number, number][];
    }
    return null;
}

function inferMaterialName(el: any): string {
    const mat = String(el.structuralMaterial?.name || el.parameters?.['Material'] || '');
    if (/steel|w-shape|tube/i.test(mat)) return 'Steel-A992';
    if (/concrete|c30|c25/i.test(mat)) return 'Concrete-C30';
    if (/wood|timber/i.test(mat)) return 'Wood-Glulam';
    return 'Steel-A992';
}

/** Re-export so other modules can mint stable analytical ids if needed. */
export { uuidv4 as newAnalyticalId };
